import { useEffect, useState } from "react";
import { type NodeKey } from "lexical";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $dfs } from "@lexical/utils";
import { m } from "@vega-core/paraglide/messages";

import { useAnnotationContext } from "./annotationContext";
import { $isFootnoteNode } from "./footnoteNode";

const $footnoteOrder = (nodeKey: NodeKey): number => {
  const keys = $dfs()
    .map(({ node }) => node)
    .filter($isFootnoteNode)
    .map((node) => node.getKey());

  return keys.indexOf(nodeKey) + 1;
};

type Props = {
  nodeKey: NodeKey;
  content: string;
};

export function FootnoteNodeView({ nodeKey, content }: Props) {
  const [editor] = useLexicalComposerContext();
  const annotation = useAnnotationContext();
  const [order, setOrder] = useState(() =>
    editor.getEditorState().read(() => $footnoteOrder(nodeKey)),
  );
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());

  useEffect(
    () =>
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => setOrder($footnoteOrder(nodeKey)));
      }),
    [editor, nodeKey],
  );

  useEffect(() => editor.registerEditableListener(setIsEditable), [editor]);

  const label = `(${m.common_footnote_see()} ${order})`;

  if (!isEditable) {
    return (
      <span
        className="align-baseline text-brand-default underline body-small-medium"
        contentEditable={false}
        data-testid="footnote-marker"
      >
        {label}
      </span>
    );
  }

  return (
    <button
      className="align-baseline text-brand-default underline body-small-medium"
      contentEditable={false}
      data-testid="footnote-marker"
      onClick={() => annotation?.openEdit("footnote", nodeKey, content)}
      type="button"
    >
      {label}
    </button>
  );
}
